'use client'
import { IErrorReturn, IResumeCard } from '@/lib/interfaces';
import { useKindeBrowserClient } from "@kinde-oss/kinde-auth-nextjs";
import { useRouter } from "next/navigation";

import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table"
import { Input } from "@/components/ui/input"
import React, { useEffect, useState } from 'react';
import { TDraftElementsCategory, VDraftsTableColumns, VResumesTableColumns } from '@/data/types';
import { resumeColumns } from '@/lib/tables-defs';
import fetchAndCreateResumeCards from '@/lib/fetchSavedResumes';
import ColumnsSelector from '../saved-drafts/ColumnsSelector';
import TableBlock from '../saved-drafts/TableBlock';
import PrevNextButtons from '../saved-drafts/PrevNextButtons';
import Link from 'next/link';
import LoginRedirect from '@/components/LoginRedirect';
import AdSterraNativeBanner from '@/components/adsterra-ui/adsterra-native-banner';
import AdSterraIFrameBannerAuto from '@/components/adsterra-ui/adsterra-ifram-auto';

function SavedResumesWrapper() {
  const router = useRouter();

  const [userId, setUserId] = useState<string>('');
  const { user, isLoading, isAuthenticated } = useKindeBrowserClient();
  const [ resumes, setResumes ] = useState<(IResumeCard | IErrorReturn)[]>([]);
  const [ dataFetched, setDataFetched ] = useState<boolean>(false);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const table = useReactTable({
    data: resumes,
    columns: resumeColumns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });
  
  useEffect(() => {
    const fetchDrafts = async (desData: { userId: string, count: number }) => {
      const tResumes: (IResumeCard | IErrorReturn)[] = 
        await fetchAndCreateResumeCards(desData, TDraftElementsCategory.FULL) as (IResumeCard | IErrorReturn)[];
      setResumes(prev => ([...tResumes]))
    }

    if (!isLoading && isAuthenticated) {
      setUserId(user!.id);
      const desData = { userId: user!.id, count: 0 };
      fetchDrafts(desData);
      setDataFetched(true);
    } else if(!isLoading && !isAuthenticated) {
        router.push("/api/auth/login");
    }
  }, [isLoading, isAuthenticated]);

  return (
    <>
      <div className="space-y-8">
        <div className='text-lg'>
          <Link className='underline' href={"/"}>Home</Link>
          <span className='pl-2 pr-2'>{">"}</span>
          <Link className='underline' href={"/dashboard"}>Dashbord</Link>
          <span className='pl-2 pr-2'>{"> Saved Resumes"}</span>
        </div>
        
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
            Saved Resumes
          </h1>
        </div>

        <AdSterraNativeBanner />

        <div className="w-full">
          <div className="flex items-center py-4">
            <Input
              placeholder="Filter resume by role..."
              value={(table.getColumn("role")?.getFilterValue() as string) ?? ""}
              onChange={(event) =>
                table.getColumn("role")?.setFilterValue(event.target.value)
              }
              className="max-w-sm"
            />
            <ColumnsSelector table={table} VDraftsTableColumns={VDraftsTableColumns} />
          </div>

          <TableBlock isLoading={isLoading} dataFetched={dataFetched} table={table} />
          
          <PrevNextButtons table={table} />
        </div>
      </div>

      <AdSterraIFrameBannerAuto position='bottom' />

      {/* 2. THE OVERLAY (Only shows if not logged in) */}
      {(!isAuthenticated && !isLoading) && (
        <LoginRedirect message="to analyze this job description" />
      )}
    </>
  );
}

export default SavedResumesWrapper